<template>
	<div class="content-wrapper">
		<nav aria-label="breadcrumb" class="compact-breadcrumb-nav">
			<ol class="breadcrumb">
				<li class="breadcrumb-item">
					<a href="#">
						<i class="fa fa-home"></i>
						Dashboard
					</a>
				</li>
				<li class="breadcrumb-item active">Branch Wallet Balance Report</li>
				<li class="breadcrumb-right d-flex align-items-center flex-wrap">
					<div class="breadcrumb-search-group">
						<div class="form-group mb-0">
							<Customer :class="{ 'is-invalid': errors.customer_id }"
								:customClass="{ 'is-invalid': errors.customer_id }" :initialize="meta.customer_name"
								@selectCustomer="selectCustomer($event)" ref="customer_id"></Customer>
							<span v-if="errors.customer_id" class="invalid-feedback d-block">{{
								errors.customer_id[0] }}</span>
						</div>
						<button class="btn btn-sm btn-primary" @click="search"><i class="fa fa-search mr-1"></i>Generate
							Report</button>
						<button class="btn btn-sm btn-danger" @click="discard"><i
								class="fa fa-times mr-1"></i>Discard</button>
						<a type="button" class="btn btn-sm btn-success" target="_blank"
							:href="$store.getters.pdfUrl + 'export-branch-wallet?branch_id=' + (meta.branch_id || '') + '&keyword=' + meta.keyword + '&order_by=' + meta.order_by + '&can_change_branch=' + (canChangeBranch ? 1 : 0)">
							<i class="fa fa-download mr-1"></i>Download Report
						</a>
					</div>
				</li>
			</ol>
		</nav>
		<section class="content">
			<div class="container-fluid">
				<div class="row justify-content-md-center">
					<div class="col-sm-12">
						<div class="card">
							<div class="table-container">
								<table class="table table-striped table-bordered table-sm">
									<thead>
										<tr>
											<th class="text-center">#</th>
											<th @click="sort('customer_no')" class="text-center"
												style="cursor: pointer;">
												Customer
												<span>
													<i v-if="meta.keyword == 'customer_no' && meta.order_by == 'asc'"
														class="fas fa-sort-up"></i>
													<i v-else-if="meta.keyword == 'customer_no' && meta.order_by == 'desc'"
														class="fas fa-sort-down"></i>
													<i v-else class="fas fa-sort"></i>
												</span>
											</th>
											<!-- Credit Limit header: show all branches if has change_branch permission, else single column -->
											<template v-if="canChangeBranch">
												<th v-for="b in branches" :key="'clh' + b.branch_id" class="text-center"
													@click="sort('branch_' + b.branch_id + '_credit_limit')"
													style="cursor: pointer;">
													<div>{{ b.description }}</div>
													<div style="font-size: 0.85em; font-weight: normal;">Credit
														Limit</div>
													<span>
														<i v-if="meta.keyword == 'branch_' + b.branch_id + '_credit_limit' && meta.order_by == 'asc'"
															class="fas fa-sort-up"></i>
														<i v-else-if="meta.keyword == 'branch_' + b.branch_id + '_credit_limit' && meta.order_by == 'desc'"
															class="fas fa-sort-down"></i>
														<i v-else class="fas fa-sort"></i>
													</span>
												</th>
											</template>
											<template v-else>
												<th class="text-center" @click="sort('credit_limit')">Credit
													Limit
													<span>
														<i v-if="meta.keyword == 'credit_limit' && meta.order_by == 'asc'"
															class="fas fa-sort-up"></i>
														<i v-else-if="meta.keyword == 'credit_limit' && meta.order_by == 'desc'"
															class="fas fa-sort-down"></i>
														<i v-else class="fas fa-sort"></i>
													</span>
												</th>
											</template>
											<th colspan="2" class="text-center" @click="sort('wallet_balance')">
												<div>Wallet Balance</div>
												<div style="font-size: 0.85em; font-weight: normal;">
													<div>Payable: {{ total_payable }}</div>
													<div>Receivable: {{ total_receivable }}</div>
												</div>
												<span>
													<i v-if="meta.keyword == 'wallet_balance' && meta.order_by == 'asc'"
														class="fas fa-sort-up"></i>
													<i v-else-if="meta.keyword == 'wallet_balance' && meta.order_by == 'desc'"
														class="fas fa-sort-down"></i>
													<i v-else class="fas fa-sort"></i>
												</span>
											</th>
											<!-- Removed Branch Wallet Balance single column when canChangeBranch is true -->
											<th v-can="'change_branch'" v-for="b in branches" :key="'bh' + b.branch_id"
												class="text-center" @click="sort('branch_' + b.branch_id + '_balance')"
												style="cursor: pointer;">
												<div>{{ b.description }}</div>
												<div style="font-size: 0.85em; font-weight: normal;">
													<div>Payable: {{ (branchTotals[b.branch_id]?.payable
														|| 0).toFixed(0) }}</div>
													<div>Receivable: {{
														(branchTotals[b.branch_id]?.receivable ||
															0).toFixed(0) }}</div>
												</div>
												<span>
													<i v-if="meta.keyword == 'branch_' + b.branch_id + '_balance' && meta.order_by == 'asc'"
														class="fas fa-sort-up"></i>
													<i v-else-if="meta.keyword == 'branch_' + b.branch_id + '_balance' && meta.order_by == 'desc'"
														class="fas fa-sort-down"></i>
													<i v-else class="fas fa-sort"></i>
												</span>
											</th>
											<th class="text-center">Action</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(customer, key) in customers" :key="key">
											<td class="text-center">{{ meta.from + key }}</td>
											<td class="text-center">
												<router-link :to="'/customers/' + customer.customer_id + '/view'"
													class="customer-link">
													<div class="customer-name">{{ customer.customer_name }}
													</div>
													<div class="customer-no">{{ customer.customer_no }}</div>
												</router-link>
											</td>
											<!-- Credit Limit cell: show all branches if has change_branch permission, else single column -->
											<template v-if="canChangeBranch">
												<td v-for="b in branches"
													:key="'cl' + customer.customer_id + '_' + b.branch_id"
													class="text-center">
													{{ formatCreditLimit(customer['branch_' + b.branch_id +
													'_credit_limit']) }}
												</td>
											</template>
											<template v-else>
												<td class="text-center">{{ formatCreditLimit(customer.credit_limit) }}
												</td>
											</template>
											<td class="text-center" v-if="customer.wallet_balance > 0">
												{{ (customer.wallet_balance * 1).toFixed(0) }}
											</td>
											<td class="text-center" v-else>-</td>
											<td class="text-center" v-if="customer.wallet_balance < 0">
												{{ (customer.wallet_balance * 1).toFixed(0) }}
											</td>
											<td class="text-center" v-else>-</td>
											<!-- Removed Branch Wallet Balance cell for canChangeBranch -->
											<td v-can="'change_branch'" v-for="b in branches"
												:key="'br' + customer.customer_id + '_' + b.branch_id"
												class="text-center">
												{{ customer['branch_' + b.branch_id + '_balance'] !== undefined
													?
													(customer['branch_' + b.branch_id + '_balance'] * 1).toFixed(0)
													:
													'-' }}
											</td>
											<td class="text-center">
												<button v-can="'customers.credit_limit'"
													class="btn btn-xs btn-outline-secondary mr-1"
													@click.prevent="openCreditLimitModal(customer)"
													title="Update Branch Credit Limit">
													<i class="fa fa-credit-card"></i>
												</button>
												<button class="btn btn-xs btn-outline-success"
													@click.prevent="callCustomer(customer.mobile_no)">
													<i class="fa fa-phone"></i>
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div class="card-footer pagination-wrapper">
								<div class="float-right">
									<Pagination :maxPage="meta.maxPage" :totalPages="meta.lastPage"
										:currentPage="meta.page" @pagechanged="onPageChange" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Update Branch Credit Limit Modal -->
		<div class="modal fade" id="update_credit_limit" ref="updateCreditLimitModal">
			<div class="modal-dialog" :class="{ 'modal-lg': canChangeBranch }">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Update Branch Credit Limit - {{
							selectedCustomerForCreditLimit?.customer_name ||
							'' }}</h5>
						<button type="button" class="close" data-dismiss="modal" aria-label="Close"
							@click="closeCreditLimitModal">
							<span aria-hidden="true">&times;</span>
						</button>
					</div>
					<div class="modal-body">
						<!-- Single branch credit limit (for users without change_branch permission) -->
						<div class="row" v-if="!canChangeBranch">
							<div class="col-sm-12">
								<div class="form-group">
									<label for="branch_credit_limit">Branch Credit Limit *</label>
									<input type="number" class="form-control form-control-sm"
										:class="{ 'is-invalid': errors.branch_credit_limit }"
										placeholder="Branch Credit Limit" v-model="branch_credit_limit"
										ref="branch_credit_limit" />
									<span v-if="errors.branch_credit_limit" class="invalid-feedback">{{
										errors.branch_credit_limit[0]
									}}</span>
								</div>
							</div>
						</div>

						<!-- Multiple branch credit limits (for users with change_branch permission) -->
						<div v-if="canChangeBranch">
							<div class="row" v-if="branchCreditLimits.length === 0">
								<div class="col-sm-12">
									<div class="alert alert-info">
										Loading branches...
									</div>
								</div>
							</div>
							<div class="row" v-else>
								<div class="col-sm-12">
									<div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
										<table class="table table-bordered table-sm">
											<thead>
												<tr>
													<th>Branch Name</th>
													<th>Current Credit Limit</th>
													<th>New Credit Limit</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="(branchLimit, index) in branchCreditLimits" :key="index">
													<td>{{ branchLimit.branch_name }}</td>
													<td>{{ branchLimit.current_limit || 0 }}</td>
													<td>
														<input type="number" class="form-control form-control-sm"
															:class="{ 'is-invalid': errors['branch_credit_limit_' + branchLimit.branch_id] }"
															v-model="branchLimit.new_limit"
															:placeholder="'Enter credit limit for ' + branchLimit.branch_name" />
														<span
															v-if="errors['branch_credit_limit_' + branchLimit.branch_id]"
															class="invalid-feedback">
															{{ errors['branch_credit_limit_' + branchLimit.branch_id][0]
															}}
														</span>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-danger" @click="closeCreditLimitModal"
							ref="CloseCreditLimit">
							<i class="fa fa-times"></i> Close
						</button>
						<button type="button" class="btn btn-success" @click="updateBranchCreditLimit()">
							<i class="fa fa-save"></i> Submit
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Pagination from "@/components/Pagination.vue";
let Customer = require('../../components/Customer.vue').default;
export default {
	name: "User.Index",
	components: { Pagination, Customer },
	data() {
		return {
			status: true,
			canChangeBranch: false,
			meta: {
				branch_id: this.$store.getters.user.branch_id,
				report_type: '',
				customer_id: '',
				customer_no: '',
				customer_name: '',
				session_log_id: '',
				order_by: "asc",
				keyword: "customer_no",
				per_page: 50,
				totalRows: 0,
				page: 1,
				lastPage: 1,
				from: 1,
				maxPage: 1,
			},
			selectedBranch: "",
			report_type: '',
			total_wallet_balance: 0,
			total_branch_wallet_balance: 0,
			total_payable: 0,
			total_receivable: 0,
			session_logs: [],
			errors: [],
			customers: [],
			branches: [],
			transferForm: {
				customer_id: '',
				customer_name: '',
				from_branch_id: '',
				to_branch_id: '',
				amount: '',
				remarks: ''
			},
			transferLoading: false,
			showTransferModal: false,
			branchTotals: {}, // Store payable and receivable for each branch from backend
			selectedCustomerForCreditLimit: null, // Customer selected for credit limit update
			branch_credit_limit: 0, // Single branch credit limit (for users without change_branch permission)
			branchCreditLimits: [], // Array of branch credit limits (for users with change_branch permission)
		};
	},
	mounted() {
		let vm = this;
		vm.getBranches();
		// Set branch_id based on permission: if can change_branch, allow all (''), else default to user's branch
		// Note: canChangeBranch will be updated from backend response, this is just initial setup
		try {
			let permissions = vm.$store.getters.permissions || [];
			let canChange = permissions.some(function (el) {
				return el.ability && el.ability.ability == 'change_branch' && el.ability?.module?.application_type == 'Web';
			});
			vm.meta.branch_id = canChange ? '' : vm.$store.getters.user.branch_id;
			// Don't set canChangeBranch here - wait for backend response
			vm.canChangeBranch = false; // Will be updated from backend
		} catch (e) {
			vm.meta.branch_id = vm.$store.getters.user.branch_id;
			vm.canChangeBranch = false;
		}
		// vm.discard();
	},


	methods: {
		formatCurrency(amount) {
			const formattedAmount = new Intl.NumberFormat('en-IN', {
				style: 'currency',
				currency: 'INR'
			}).format(amount);

			return formattedAmount;
		},
		formatCreditLimit(value) {
			// Handle null, undefined, empty string, or invalid values
			if (value === null || value === undefined || value === '' || isNaN(value)) {
				return '-';
			}
			// Convert to number and format to 0 decimal places
			const numValue = parseFloat(value) || 0;
			return numValue.toFixed(0);
		},
		index() {
			let vm = this;
			let loader = vm.$loading.show();
			vm.$store.dispatch("post", { uri: "paginateCustomerWithBranchBalance", data: vm.meta })
				.then(function (response) {
					vm.customers = response.data.customers.data;
					vm.total_wallet_balance = response.data.total_wallet_balance;
					vm.total_branch_wallet_balance = response.data.total_branch_wallet_balance;
					vm.total_payable = response.data.total_payable || 0;
					vm.total_receivable = response.data.total_receivable || 0;
					vm.report_type = response.data.report_type;
					if (response.data.branches) {
						vm.branches = response.data.branches;
					}
					// Store branch-wise totals from backend
					vm.branchTotals = response.data.branch_totals || {};
					// Update canChangeBranch from backend response (backend is source of truth)
					// ALWAYS use backend value, don't check frontend permissions
					vm.canChangeBranch = response.data.can_change_branch === true || response.data.can_change_branch === 1 || response.data.can_change_branch === '1';
					vm.meta.totalRows = response.data.customers.total;
					vm.meta.lastPage = response.data.customers.last_page;
					vm.meta.from = response.data.customers.from;
					vm.meta.maxPage = vm.meta.lastPage >= 3 ? 3 : vm.meta.lastPage;
					loader.hide();
				})
				.catch(function (error) {
					loader.hide();
					vm.errors = error.response.data.errors;
					vm.$store.dispatch("error", error.response.data.message);
				});
		},
		callCustomer(to) {
			let vm = this;
			let loader = vm.$loading.show();
			let data = {
				From: '0' + vm.$store.getters.user.mobile_no,
				To: '0' + to
			}
			let uri = { uri: "customerCallConnect", data: data };
			vm.$store.dispatch("post", uri)
				.then(function () {
					loader.hide();
					vm.$store.dispatch("success", "Call connected, please wait for sometime");
				})
				.catch(function (error) {
					loader.hide();
					vm.errors = error.response.data.errors;
					vm.$store.dispatch("error", error.response.data.message);
				});
		},
		getBranches() {
			let vm = this;
			vm.$store
				.dispatch("post", { uri: "getBranches" })
				.then(function (response) {
					vm.branches = response.data.data;
				})
				.catch(function (error) {
					vm.errors = error.response.data.errors;
					vm.$store.dispatch("error", error.response.data.message);
				});
		},
		handleBranchChange() {
			this.meta.branch_id = this.selectedBranch ? this.selectedBranch : this.$store.getters.user.branch_id;
			this.meta.page = 1;
			this.index();
		},
		onBranchChange() {
			this.meta.page = 1;
			this.index();
		},
		discard() {
			let vm = this;
			// Reset customer filters
			vm.meta.report_type = '';
			vm.meta.customer_id = '';
			vm.meta.customer_no = '';
			vm.meta.customer_name = '';
			vm.meta.session_log_id = '';

			// Reset pagination
			vm.meta.page = 1;

			// Reset sorting to default
			vm.meta.keyword = 'customer_no';
			vm.meta.order_by = 'asc';

			// Clear customer component if it has a clear method
			if (vm.$refs.customer_id && typeof vm.$refs.customer_id.clear === 'function') {
				vm.$refs.customer_id.clear();
			}

			// Call API to fetch data
			vm.search();
		},
		selectCustomer(e) {
			let vm = this;
			const customer = e.target.value;
			vm.meta.customer_id = customer.customer_id;
			vm.meta.customer_no = customer.customer_no;
			vm.meta.customer_name = customer.customer_no + ' - ' + customer.customer_name;
			vm.meta.session_log_id = customer.session_log_id;
		},
		onPageChange(page) {
			this.meta.page = page;
			this.index();
		},
		search() {
			let vm = this;
			vm.meta.page = 1;
			vm.index()
		},
		sort(field) {
			this.meta.keyword = field;
			this.meta.order_by = this.meta.order_by == 'asc' ? 'desc' : 'asc';
			this.search();
		},
		openCreditLimitModal(customer) {
			let vm = this;
			vm.selectedCustomerForCreditLimit = customer;
			vm.errors = {};

			if (vm.canChangeBranch) {
				vm.prepareBranchCreditLimits(customer);
			} else {
				// For users without change_branch permission, fetch current customer data to get credit limit
				vm.$store.dispatch("post", {
					uri: "showCustomer",
					data: { customer_id: customer.customer_id }
				})
					.then(function (response) {
						vm.branch_credit_limit = response.data.data?.customer_credit_limit?.credit_limit || 0;
						// Show modal using ref
						vm.$nextTick(() => {
							if (vm.$refs.updateCreditLimitModal) {
								const modalElement = vm.$refs.updateCreditLimitModal;
								modalElement.classList.add('show');
								modalElement.style.display = 'block';
								modalElement.setAttribute('aria-hidden', 'false');
								document.body.classList.add('modal-open');
								// Add backdrop
								const backdrop = document.createElement('div');
								backdrop.className = 'modal-backdrop fade show';
								backdrop.setAttribute('id', 'updateCreditLimitModalBackdrop');
								document.body.appendChild(backdrop);
							}
						});
					})
					.catch(function (error) {
						vm.errors = error.response?.data?.errors || {};
						vm.$store.dispatch("error", error.response?.data?.message || "Error loading customer data");
					});
			}
		},
		prepareBranchCreditLimits(customer) {
			let vm = this;
			vm.branchCreditLimits = [];

			// Fetch all branches
			vm.$store.dispatch("post", { uri: "getBranches" })
				.then(function (response) {
					let branches = response.data.data || [];
					// Filter out deleted branches
					branches = branches.filter(b => !b.deleted_at);

					// Fetch customer data to get existing credit limits
					vm.$store.dispatch("post", {
						uri: "showCustomer",
						data: { customer_id: customer.customer_id }
					})
						.then(function (customerResponse) {
							let customerData = customerResponse.data.data;

							// Create a map of existing credit limits for quick lookup
							let creditLimitMap = {};
							if (customerData?.customer_credit_limits) {
								customerData.customer_credit_limits.forEach(creditLimit => {
									creditLimitMap[creditLimit.branch_id] = creditLimit.credit_limit || 0;
								});
							}

							// Prepare branch credit limits array with all branches
							branches.forEach(branch => {
								vm.branchCreditLimits.push({
									branch_id: branch.branch_id,
									branch_name: branch.branch_name || branch.description || 'Unknown Branch',
									current_limit: creditLimitMap[branch.branch_id] || 0,
									new_limit: creditLimitMap[branch.branch_id] || 0
								});
							});

							// Show modal after data is loaded
							vm.$nextTick(() => {
								if (vm.$refs.updateCreditLimitModal) {
									const modalElement = vm.$refs.updateCreditLimitModal;
									modalElement.classList.add('show');
									modalElement.style.display = 'block';
									modalElement.setAttribute('aria-hidden', 'false');
									document.body.classList.add('modal-open');
									// Add backdrop
									const backdrop = document.createElement('div');
									backdrop.className = 'modal-backdrop fade show';
									backdrop.setAttribute('id', 'updateCreditLimitModalBackdrop');
									document.body.appendChild(backdrop);
								}
							});
						})
						.catch(function (error) {
							vm.errors = error.response?.data?.errors || {};
							vm.$store.dispatch("error", error.response?.data?.message || "Error loading customer data");
						});
				})
				.catch(function () {
					vm.$store.dispatch("error", "Error fetching branches");
				});
		},
		updateBranchCreditLimit() {
			let vm = this;
			if (!vm.selectedCustomerForCreditLimit) {
				vm.$store.dispatch("error", "No customer selected");
				return;
			}

			let loader = vm.$loading.show();

			if (vm.canChangeBranch && vm.branchCreditLimits.length > 0) {
				// Filter branches where credit limit has changed
				let branchesToUpdate = vm.branchCreditLimits.filter(branchLimit => {
					// Convert to numbers for comparison
					let currentLimit = parseFloat(branchLimit.current_limit) || 0;
					let newLimit = parseFloat(branchLimit.new_limit) || 0;
					// Only update if value has changed
					return currentLimit !== newLimit;
				});

				if (branchesToUpdate.length === 0) {
					loader.hide();
					vm.$store.dispatch("info", "No changes detected. All credit limits are up to date.");
					vm.closeCreditLimitModal();
					return;
				}

				// Update only branches where credit limit has changed
				let promises = branchesToUpdate.map(branchLimit => {
					return vm.$store.dispatch("post", {
						uri: "updateCustomerBranchCreditLimit",
						data: {
							customer_id: vm.selectedCustomerForCreditLimit.customer_id,
							credit_limit: branchLimit.new_limit,
							branch_id: branchLimit.branch_id,
							user_id: vm.$store.getters.user.user_id,
						}
					});
				});

				Promise.all(promises).then(function () {
					loader.hide();
					vm.$store.dispatch("success", `Branch Credit Limits are successfully updated for ${branchesToUpdate.length} branch(es)`);
					vm.closeCreditLimitModal();
					// Refresh the report
					vm.index();
				}).catch(function (error) {
					loader.hide();
					vm.errors = error.response?.data?.errors || {};
					vm.$store.dispatch("error", error.response?.data?.message || "Error updating branch credit limits");
				});
			} else {
				// Update single branch (current branch) for users without change_branch permission
				let uri = {
					uri: "updateCustomerBranchCreditLimit",
					data: {
						customer_id: vm.selectedCustomerForCreditLimit.customer_id,
						credit_limit: vm.branch_credit_limit,
						branch_id: vm.$store.getters.user.branch_id,
						user_id: vm.$store.getters.user.user_id,
					}
				};
				vm.$store.dispatch("post", uri).then(function () {
					loader.hide();
					vm.$store.dispatch("success", "Branch Credit Limit is successfully updated");
					vm.closeCreditLimitModal();
					// Refresh the report
					vm.index();
				}).catch(function (error) {
					loader.hide();
					vm.errors = error.response?.data?.errors || {};
					vm.$store.dispatch("error", error.response?.data?.message || "Error updating branch credit limit");
				});
			}
		},
		closeCreditLimitModal() {
			let vm = this;
			if (vm.$refs.updateCreditLimitModal) {
				const modalElement = vm.$refs.updateCreditLimitModal;
				modalElement.classList.remove('show');
				modalElement.style.display = 'none';
				modalElement.setAttribute('aria-hidden', 'true');
				document.body.classList.remove('modal-open');
				// Remove backdrop
				const backdrop = document.getElementById('updateCreditLimitModalBackdrop');
				if (backdrop) {
					backdrop.remove();
				}
			}
			// Reset form when closing modal
			vm.selectedCustomerForCreditLimit = null;
			vm.branch_credit_limit = 0;
			vm.branchCreditLimits = [];
			vm.errors = {};
		},
	},
};
</script>

<style scoped>
/* Compact breadcrumb */
.compact-breadcrumb-nav {
	margin-bottom: 0.5rem !important;
}

.compact-breadcrumb-nav .breadcrumb {
	margin-bottom: 0 !important;
	padding: 0.5rem 1rem !important;
	align-items: center;
}

.breadcrumb-search-group {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5rem;
}

.breadcrumb-search-group .form-group {
	margin-bottom: 0 !important;
	display: flex;
	align-items: center;
	min-width: 300px;
}

.breadcrumb-search-group .form-group label {
	margin-bottom: 0;
	margin-right: 0.5rem;
	white-space: nowrap;
}

.breadcrumb-search-group .form-group>div {
	display: inline-block;
	width: 100%;
	min-width: 250px;
}

.breadcrumb-search-group .form-group>div>div {
	width: 100%;
}

/* Increase table height - more space since form moved to breadcrumb and header removed */
.table-container {
	max-height: calc(100vh - 238px);
	overflow-y: auto;
	overflow-x: auto;
	position: relative;
	width: 100%;
}

.table-container .table {
	width: 100%;
	margin-bottom: 0;
	table-layout: auto;
}

/* Make table more compact to reduce horizontal scrolling */
.table-container .table th:first-child,
.table-container .table td:first-child {
	width: 40px;
	min-width: 40px;
}

.table-container .table th:nth-child(2),
.table-container .table td:nth-child(2) {
	max-width: 180px;
	min-width: 150px;
}

/* Reduce pagination spacing */
.card-footer.pagination-wrapper {
	padding: 0.5rem 1rem !important;
	margin-top: 0 !important;
	margin-bottom: 0 !important;
}

.table-container .table thead {
	position: sticky;
	top: 0;
	z-index: 100;
	background-color: #fff;
}

.table-container .table thead th {
	background-color: #fff !important;
	border-bottom: 2px solid #dee2e6;
	position: sticky;
	top: 0;
	z-index: 101;
	box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);
}

.table th,
.table td {
	vertical-align: middle;
	padding: 0.2rem 0.1rem;
	font-size: 0.8rem;
	white-space: normal;
	word-wrap: break-word;
}

.table th {
	font-size: 0.75rem;
	padding: 0.25rem 0.1rem;
}

.customer-link {
	text-decoration: none;
	color: inherit;
	display: inline-block;
	padding: 4px 0;
	transition: all 0.2s;
	background: transparent;
	text-align: center;
}

.customer-link:hover {
	text-decoration: none;
	background: transparent;
}

.customer-link:hover .customer-name {
	color: #007bff;
}

.customer-link:hover .customer-no {
	color: #0056b3;
}

.customer-name {
	color: #495057;
	font-weight: 600;
	font-size: 1em;
	display: block;
	line-height: 1.4;
	margin-bottom: 2px;
	text-align: center;
}

.customer-no {
	font-weight: 400;
	color: #6c757d;
	font-size: 0.85em;
	display: block;
	line-height: 1.2;
	text-align: center;
}
</style>
